import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type CalView = 'day' | 'week' | 'month';

interface CalendarNavState {
  /** YYYY-MM-DD anchor date for the current calendar view. */
  anchorISO: string;
  view: CalView;
}

/** YYYY-MM-DD in the browser's local timezone. Never use toISOString() — that's UTC. */
function toDateISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function shiftDate(isoStr: string, deltaDays: number): string {
  const d = new Date(isoStr + 'T12:00:00');
  d.setDate(d.getDate() + deltaDays);
  return toDateISO(d);
}

function shiftMonth(isoStr: string, deltaMonths: number): string {
  const d = new Date(isoStr + 'T12:00:00');
  d.setMonth(d.getMonth() + deltaMonths);
  return toDateISO(d);
}

const calendarNavSlice = createSlice({
  name: 'calendarNav',
  initialState: {
    anchorISO: toDateISO(new Date()),
    view: 'week' as CalView,
  } as CalendarNavState,
  reducers: {
    setCalendarView(state, action: PayloadAction<CalView>) {
      state.view = action.payload;
    },
    setCalendarAnchor(state, action: PayloadAction<string>) {
      state.anchorISO = action.payload;
    },
    /** Dispatched by toolbar clicks and by the navigate_calendar SAM tool. */
    navigateCalendarAction(
      state,
      action: PayloadAction<{
        direction?: string | null;
        unit?: string | null;
        view?: string | null;
        date?: string | null;
      }>
    ) {
      const { direction: dir, unit, view, date } = action.payload;
      if (view === 'day' || view === 'week' || view === 'month') {
        state.view = view;
      }
      if (date) {
        state.anchorISO = date.split('T')[0];
        return;
      }
      if (dir === 'today') {
        state.anchorISO = toDateISO(new Date());
        return;
      }
      if (dir === 'next' || dir === 'prev') {
        const delta = dir === 'next' ? 1 : -1;
        const effectiveUnit = unit || state.view;
        if (effectiveUnit === 'month') {
          state.anchorISO = shiftMonth(state.anchorISO, delta);
        } else if (effectiveUnit === 'week') {
          state.anchorISO = shiftDate(state.anchorISO, delta * 7);
        } else {
          state.anchorISO = shiftDate(state.anchorISO, delta);
        }
      }
    },
  },
});

export const { setCalendarView, setCalendarAnchor, navigateCalendarAction } = calendarNavSlice.actions;
export default calendarNavSlice.reducer;
